export type IErrorResponse = {
  type: string
  title: string
  status: number
  detail: string
  instance: string
  traceId: string | null
  error: string | null
}

export type IRequestErrorWithErrors = {
  type: string
  title: string
  status: 0
  detail: string
  instance: string
  errors: {
    additionalProp1: [string]
    additionalProp2: [string]
    additionalProp3: [string]
  }
  additionalProp1: string
  additionalProp2: string
  additionalProp3: string
}

export enum OperationType {
  SBP_INCOME_TRANSFER = "SbpIncomeTransfer",
  SBP_OUTCOME_TRANSFER = "SbpOutcomeTransfer",
  SBP_PAYOUT = "SbpPayout",
  SBP_REFUND = "SbpRefund",
  SBP_OUTCOME_PAYMENT = "SbpOutcomePayment",
  TRANSFER = "Transfer",
  INCOME_TRANSFER = "IncomeTransfer",
  BALANCE_ADJUSTMENT_PAYMENT = "BalanceAdjustmentPayment",
  INCOME_TRANSFER_REFUND = "IncomeTransferRefund",
  OUTCOME_TRANSFER = "OutcomeTransfer",
  OUTCOME_TRANSFER_REFUND = "OutcomeTransferRefund",
  AGENT_PAYMENT = "AgentPayment",
  AGENT_PAYMENT_REFUND = "AgentPaymentRefund",
  PROVIDER_PAYMENT = "ProviderPayment",
  PROVIDER_PAYMENT_REFUND = "ProviderPaymentRefund",
  REFILL_PAYMENT = "RefillPayment",
  REFILL_PAYMENT_REFUND = "RefillPaymentRefund",
  SERVICE_PAYMENT = "ServicePayment",
  SERVICE_PAYMENT_REFUND = "ServicePaymentRefund",
}

export enum OperationStatus {
  CREATED = "Created",
  CREATE_ERROR = "CreateError",
  ACCEPTED = "Accepted",
  ACCEPT_ERROR = "AcceptError",
  REJECTED = "Rejected",
  CANCELED = "Canceled",
}

export enum TransferRequestStatus {
  CREATING = "Creating",
  CREATED = "Created",
  CREATE_ERROR = "CreateError",
  ACCEPTING = "Accepting",
  ACCEPTED = "Accepted",
  ACCEPT_ERROR = "AcceptError",
  REJECTED = "Rejected",
}

export type IOperationItem = {
  operationId: string
  contextOperationId: string
  type: OperationType
  masterField: string
  fields: {
    name: string
    value: string
  }[]
  amount: number
  topCommissionAmount: number
  bottomCommissionAmount: number
  status: OperationStatus
  createdOn: string
  updatedOn: string
}

export enum BankRole {
  RECEIVER = "Receiver",
  SENDER = "Sender",
}

export enum BankScenario {
  C2C_PUSH = "C2CPush",
  M2M_PUSH = "Me2MePush",
  M2M_PULL = "Me2MePull",
  C2B_PUSH = "C2B",
}

export enum AuthResults {
  AUTHORIZED = "Authorized",
  OTP_FLOW_REQUIRED = "OtpFlowRequired",
  UNAUTHORIZED = "Unauthorized",
  ERROR =  "Error",
}

export type IAuthRequest = {
  fingerprint: string
  url: string
}

export type IAuthResponse = {
  customerId: string
  oneTimePasswordRequestId: string
  token: string
  result: AuthResults
}

export type IAuthResponse2 = {
  result: AuthResults
  token: string
  errorDetails?: {
    code: string
    description: string
  }
}

export enum AuthOtpResults {
  AUTHORIZED = "Authorized",
  ERROR = "Error",
}

export type IAuthOtpRequest = {
  code: string
  fingerprint: string
  url: string
}

export type IAuthOtpResponse = {
  token: string
}

export type IAuthOtpResponse2 = {
  result: AuthOtpResults
  token: string
  errorDetails?: {
    code: string
    description: string
  }
}

export enum RequestOtpResults {
  WAIT = "Wait",
  SENT = "Sent",
  ERROR = "Error",
}

export type IRequestOtpResponse = {
  result: RequestOtpResults
  errorDetails?: {
    code: string
    description: string
  }
}

export type IAuthOtpTokenError = {
  type: string
  title: string
  status: 0
  detail: string
  instance: string
  errors: {
    additionalProp1: [string]
    additionalProp2: [string]
    additionalProp3: [string]
  }
  additionalProp1: string
  additionalProp2: string
  additionalProp3: string
}

export type IGeneratePageLinkResponse = {
  token: string
  url: string
}

export type IAgreementResponse = {
  gaveAgreement: boolean
  agreementDate: string | null
}

export type IPaymentLinkResponse = {
  linkingRequestId: string
}

export type IExpensesTurnover = {
  monthTurnover: number
}

export type IUserResponse = {
  userId: string
  phoneNumber: string
  firstName: string
  lastName: string
  patronymic: string
  partnerId: string
  identificationLevel: string
  isBlocked: boolean
  bankAccountNumber: string
  balance: {
    amount: number
    safeAmount: number
    heldAmount: number
    heldSafeAmount: number
    availableAmount: number
    availableSafeAmount: number
    overLimit: {
      amount: number
      safeAmount: number
    }
  }
  availableRefill: number
  availableWithdraw: number
}

export enum PaymentToolType {
  BANK_CARD = "BankCard",
  PHONE_NUMBER = "PhoneNumber",
  BANK_ACCOUNT = "BankAccount",
  SBP_ACCOUNT = "SbpAccount",
}

export type IPaymentTool = {
  paymentToolId: string
  paymentToolType: PaymentToolType
  maskedValue: string
  default: boolean
  createdOn: string
  updatedOn: string
}

export type ILinkingStatusResponse = {
  paymentToolId: string
  linkAccountRequestId: string
}

export type IBankMeta = {
  bankId: string
  title: string
  logoUrl: string
}

export type ICreateSbpOutcomeTransferRequest = {
  phoneNumber: string
  bankId: string
  amount: number
  description: string
  fingerprint: string | null
}

export type ICreateSbpOutcomeTransferResponse = {
  outcomeTransferId: string
  gaveAgreement: boolean
}

export type IGetSbpOutcomeTransferStatusResponse = {
  outcomeTransferId: string
  operationNumber: string
  bankInfo: IBankMeta
  recipientInfo: {
    phoneNumber: string
    pam: string
  }
  amount: number
  commissionAmount: number
  description: string
  status: OperationStatus
  createdOn: string
  updateOn: string
}

export type IGetSbpOutcomePaymentStatusResponse = {
  outcomePaymentId: string
  status: OperationStatus
  amount: number
  description: string
  operationNumber: string
  createdOn: string
  updatedOn: string
  recipient: {
    legalName: string
    merchantBrandName: string
    taxNumber: string
    bank: {
        bankId: string
        name: string
        nameRus: string
        logoUrl: string
    },
    accountNumber: string
  }
}

export type ICreateSbpOutcomeTransferRequestRequest = {
  bankId: string
  amount: number
  description: string
}

export type ICreateSbpOutcomeTransferRequestResponse = {
  outcomeTransferRequestId: string
}

export type IGetSbpOutcomeTransferRequestStatusResponse = {
  outcomeTransferRequestId: string
  bankInfo: IBankMeta
  customerInfo: {
    // TODO: вынести в отдельный тип
    id: string
    phoneNumber: string
  }
  amount: number
  status: TransferRequestStatus
  createdOn: string
  updateOn: string
}

export type IGetSbpIncomeTransferRequestResponse = {
  incomeTransferRequestId: string
  customerInfo: {
    id: string
    phoneNumber: string
    pam: string
  }
  amount: number
  description: string
  status: string
  rejectDetails: {
    code: string
    description: string
  }
  createdOn: string
  bankInfo: IBankMeta
}

export enum UpdateDefaultBankRequestStatus {
  CREATING = "Creating",
  CREATED = "Created",
  CREATE_ERROR = "CreateError",
  CONFIRMING = "Confirming",
  CONFIRMING_ERROR = "ConfirmingError",
  CONFIRMED = "Confirmed",
  CONFIRM_ERROR = "ConfirmError",
}

export enum UpdateDefaultBankRequestAction {
  SET = "SET",
  DELETE = "DELETE",
}

export type ICreateUpdateDefaultBankRequestRequest = {
  action: UpdateDefaultBankRequestAction
}

export type ICreateUpdateDefaultBankRequestResponse = {
  requestId: string
}

export type IConfirmUpdateDefaultBankRequestRequest = {
  requestId: string
  otp: string
}

export type IGetUpdateDefaultBankRequestResponse = {
  requestId: string
  status: UpdateDefaultBankRequestStatus
}

export type IPageResponse<T> = {
  total: number
  limit: number
  offset: number
  items: T[]
}

export type TPermissionBanksResponse = {
  permissions: string[]
}

// export type TFingerprintWindow = Window & {
//   Fingerprint?: { get: (options: any, func: (data: string) => void) => void }
// }

export type TUserLimits = {
  maxValues: {
    balanceAmount: number
    dailyAmount: number
    monthlyAmount: number
  }
  currentValues: {
    balanceAmount: number
    dailyAmount: number
    monthlyAmount: number
  }
}

export enum ApiErrorCodes {
  OPERATION_NOT_FOUND = "operation_not_found",

  PARTNER_NOT_FOUND = "partner_not_found",

  CUSTOMER_IS_BLOCKED = "customer_is_blocked",
  CUSTOMER_IS_CLOSED = "customer_is_closed",
  CUSTOMER_NOT_FOUND = "customer_not_found",
  INAPPROPRIATE_CUSTOMER_TYPE = "inappropriate_customer_type",

  UNIDENTIFIED_CUSTOMER = "unidentified_customer",

  IDENTIFICATION_REQUEST_NOT_FOUND = "identification_request_not_found",

  BANK_NOT_FOUND = "bank_not_found",

  FRAUD_DETECTED = "fraud_detected",

  OTP_ATTEMPT_FAILED = "otp_attempt_failed",
  OTP_ATTEMPTS_EXCEEDED = "otp_attempts_exceeded",
  OTP_FLOW_FAILED = "otp_flow_failed",

  INVALID_CUSTOMER_DATA = "invalid_customer_data",
}

export const QRC_TYPE = {
  STATIC: "01",
  DYNAMIC: "02",
} as const

export interface QrPaymentUrlParams {
  host: string
  qrcId: string
  type: string | null
  memberId: string | null
  sum: string | null
  cur: string | null
  crc: string | null
}

export interface IQrcInfo {
  qrcId: string
  qrcType: string
  scenario: string
  amount: number
  currency: string
  description: string
  merchantBrandName: string
}